import React, { useCallback, useState } from 'react';
import { FlashOn } from '@mui/icons-material';
import { CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import { Translate, withLocalize, LocalizeContextProps } from 'react-localize-redux';
import { useSelector } from 'react-redux';
import Modal from './general/modal';
import { ReduxStore } from '../constants/types';
import { Bill, PaymentMethod } from '../pages/hospital/Billing/types';
import { getBillByUuidService, quickPayBillService } from '../services/billing';
import { InsuranceClaimsApiService } from '../pages/hospital/InsuranceClaims/services/insuranceClaimsApi';
import {
    evaluateQuickPayEligibility,
    evaluateQuickPayLineEligibility,
    getQuickPayChargeableLines,
} from '../utils/quickPayEligibility';
import { formatCurrency } from '../utils/index.jsx';

interface QuickPayButtonProps extends LocalizeContextProps {
    uuidInvestigation: string;
    billUuid: string;
    currency: string;
    languageCode: string;
    onSuccess: () => void;
    size?: 'small' | 'medium' | 'large';
    /** When false, success is only reported via onSuccess (no inline caption). */
    showInlineSuccess?: boolean;
    /**
     * SHER-453: when set, Quick Pay settles only THIS appointment's line of a shared invoice
     * (line-scoped). When omitted, legacy whole-bill Quick Pay is used.
     */
    requestServiceInvestigationId?: number | null;
}

const QuickPayButton: React.FC<QuickPayButtonProps> = ({
    uuidInvestigation,
    billUuid,
    currency,
    languageCode,
    onSuccess,
    size = 'medium',
    showInlineSuccess = true,
    requestServiceInvestigationId,
    translate,
}) => {
    const paymentMethods: PaymentMethod[] = useSelector(
        (state: ReduxStore) => state.investigations.currentInvestigation?.billingInfo?.paymentMethods ?? [],
    );
    const cashPaymentMethodId =
        paymentMethods.find((method) => method.code === 'CASH' && method.active !== false)?.id ??
        paymentMethods.find((method) => method.code === 'CASH')?.id ??
        null;

    const [loading, setLoading] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [bill, setBill] = useState<Bill | null>(null);
    const [patientAmountDue, setPatientAmountDue] = useState(0);
    const [willCloseBill, setWillCloseBill] = useState(false);
    const [chargeableLines, setChargeableLines] = useState<Bill['billItems']>([]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [confirmErrorReason, setConfirmErrorReason] = useState<string | null>(null);
    const [paying, setPaying] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const loadEligibility = useCallback(async (): Promise<boolean> => {
        setLoading(true);
        setErrorMessage(null);
        try {
            const billResponse = await getBillByUuidService(uuidInvestigation, billUuid);
            const loadedBill = billResponse.bill;
            let externalCoverage = 0;
            let claimsLoaded = true;
            try {
                const claims = await new InsuranceClaimsApiService().getClaimsForBill(
                    uuidInvestigation,
                    billUuid,
                );
                externalCoverage = claims.reduce((sum, claim) => sum + (claim.requestedAmount ?? 0), 0);
            } catch {
                claimsLoaded = false;
            }

            const lineScoped = typeof requestServiceInvestigationId === 'number';
            const eligibility = lineScoped
                ? evaluateQuickPayLineEligibility(loadedBill, requestServiceInvestigationId!, {
                      externalCoverageAmount: externalCoverage,
                      cashPaymentMethodId,
                      claimsLoaded,
                  })
                : evaluateQuickPayEligibility(loadedBill, {
                      externalCoverageAmount: externalCoverage,
                      cashPaymentMethodId,
                      claimsLoaded,
                  });
            if (!eligibility.eligible) {
                setErrorMessage(eligibility.reason ?? 'not_eligible');
                return false;
            }

            setBill(loadedBill);
            setPatientAmountDue(eligibility.patientAmountDue ?? 0);
            setWillCloseBill(Boolean(eligibility.willCloseBill));
            // Confirmation shows only the line(s) being paid: the single appointment line in
            // line-scoped mode, or the whole bill's chargeable lines in whole-bill mode.
            setChargeableLines(
                lineScoped
                    ? (loadedBill.billItems ?? []).filter(
                          (item) => item.requestServiceInvestigationId === requestServiceInvestigationId,
                      )
                    : getQuickPayChargeableLines(loadedBill),
            );
            return true;
        } catch {
            setErrorMessage('load_failed');
            return false;
        } finally {
            setLoading(false);
        }
    }, [uuidInvestigation, billUuid, cashPaymentMethodId, requestServiceInvestigationId]);

    const handleOpen = async () => {
        setConfirmErrorReason(null);
        const ok = await loadEligibility();
        if (ok) {
            setShowConfirm(true);
        }
    };

    const knownQuickPayReasons = [
        'not_eligible',
        'no_cash_method',
        'invalid_status',
        'not_single_line',
        'already_paid',
        'partial_coverage',
        'no_bill',
        'not_invoice',
        'package',
        'quantity_not_one',
        'negative_balance',
        'claims_load_failed',
        'has_special_lines',
        'line_not_on_bill',
    ] as const;

    const renderConfirmFailureReason = (reason: string) => {
        const reasonId = knownQuickPayReasons.includes(reason as (typeof knownQuickPayReasons)[number])
            ? `pages.hospital.outpatients.quick_pay.error.${reason}`
            : null;
        return (
            <Typography variant="body2" color="error">
                {reasonId ? (
                    <Translate id={reasonId} />
                ) : (
                    <Translate
                        id="pages.hospital.outpatients.quick_pay.error.pay_failed_reason"
                        data={{ reason }}
                    />
                )}
            </Typography>
        );
    };

    const handleConfirm = async () => {
        if (!bill) return;
        if (patientAmountDue > 0 && !cashPaymentMethodId) {
            setConfirmErrorReason('no_cash_method');
            return;
        }
        setPaying(true);
        setConfirmErrorReason(null);
        try {
            await quickPayBillService(
                uuidInvestigation,
                billUuid,
                patientAmountDue,
                cashPaymentMethodId ?? undefined,
                requestServiceInvestigationId,
            );
            setShowConfirm(false);
            if (showInlineSuccess) {
                setSuccessMessage(
                    translate('pages.hospital.outpatients.quick_pay.success', {
                        amount: formatCurrency(patientAmountDue, currency, languageCode),
                    }).toString(),
                );
            }
            onSuccess();
        } catch (err: unknown) {
            const apiError = err as { errorMessage?: string };
            setConfirmErrorReason(apiError?.errorMessage ?? 'pay_failed');
        } finally {
            setPaying(false);
        }
    };

    return (
        <>
            <Tooltip title={<Translate id="pages.hospital.outpatients.quick_pay.tooltip" />}>
                <span>
                    <IconButton
                        data-testid="quick-pay-button"
                        aria-label="quick-pay"
                        size={size}
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            handleOpen();
                        }}
                        disabled={loading || paying}
                    >
                        {loading ? (
                            <CircularProgress size={20} />
                        ) : (
                            <FlashOn style={{ color: '#fbc02d' }} fontSize={size} />
                        )}
                    </IconButton>
                </span>
            </Tooltip>

            {errorMessage && !showConfirm && (
                <Typography variant="caption" color="error" display="block" mt={0.5}>
                    <Translate id={`pages.hospital.outpatients.quick_pay.error.${errorMessage}`} />
                </Typography>
            )}

            {showInlineSuccess && successMessage && (
                <Typography variant="caption" color="primary" component="span" ml={0.5}>
                    {successMessage}
                </Typography>
            )}

            <Modal
                open={showConfirm}
                title={<Translate id="pages.hospital.outpatients.quick_pay.confirm_title" />}
                closeModal={() => setShowConfirm(false)}
                confirmAction={handleConfirm}
                disabled={paying}
                confirmButtonLabel="general.confirm"
            >
                <>
                    <Typography variant="body1" gutterBottom>
                        {translate('pages.hospital.outpatients.quick_pay.confirm_message', {
                            amount: formatCurrency(patientAmountDue, currency, languageCode),
                        })}
                    </Typography>
                    {willCloseBill && (
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            <Translate id="pages.hospital.outpatients.quick_pay.will_close_bill" />
                        </Typography>
                    )}
                    {chargeableLines.length > 0 && (
                        <>
                            <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                                <Translate id="pages.hospital.outpatients.quick_pay.items_heading" />
                            </Typography>
                            <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                                {chargeableLines.map((line, index) => (
                                    <li key={line.id ?? `${line.concept}-${index}`}>
                                        <Typography variant="body2" component="span">
                                            {line.concept}
                                            {' — '}
                                            {line.quantity > 1 ? `${line.quantity} × ` : ''}
                                            {formatCurrency(line.amount, currency, languageCode)}
                                        </Typography>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                    {confirmErrorReason && (
                        <>
                            <Typography variant="body2" color="error" gutterBottom sx={{ mt: 1 }}>
                                <Translate id="pages.hospital.outpatients.quick_pay.error.pay_failed" />
                            </Typography>
                            {confirmErrorReason !== 'pay_failed' &&
                                renderConfirmFailureReason(confirmErrorReason)}
                        </>
                    )}
                </>
            </Modal>
        </>
    );
};

export default withLocalize(QuickPayButton);
